import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { HelpPortableTextItem, PortableTextRendererProps } from "@/interfaces/help";

// Chose @portabletext/react (already resolved to 8.0.1 in bun.lock via apps/gfgf-studio's
// `sanity` dependency, declared here directly rather than relying on that hoist) over
// hand-rolling: it already solves grouping consecutive list-item blocks into real <ul>/<ol>
// elements, which a hand-rolled walker would otherwise have to reimplement.
//
// This component is standalone by design (GFG-684): it takes Portable Text and renders it, with
// no knowledge of the API client, routing, or any panel that embeds it, so it can move to the
// public help site unchanged.

const DEFAULT_INTERNAL_LINK_HREF = (slug: string) => `/help/${slug}`;

function buildComponents(
  internalLinkHref: (slug: string) => string,
): PortableTextComponents<HelpPortableTextItem> {
  return {
    block: {
      normal: ({ children }) => (
        <p className="text-[15px] leading-relaxed text-rodeo-black">{children}</p>
      ),
      h3: ({ children }) => (
        <h3 className="mt-6 mb-2 text-[17px] font-bold text-rodeo-black first:mt-0">{children}</h3>
      ),
    },
    list: {
      bullet: ({ children }) => (
        <ul className="list-disc space-y-1 pl-5 text-[15px] text-rodeo-black">{children}</ul>
      ),
      number: ({ children }) => (
        <ol className="list-decimal space-y-1 pl-5 text-[15px] text-rodeo-black">{children}</ol>
      ),
    },
    listItem: {
      bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
      number: ({ children }) => <li className="leading-relaxed">{children}</li>,
    },
    marks: {
      strong: ({ children }) => <strong className="font-bold">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      link: ({ value, children }) => (
        <a
          href={value?.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-dust-ink underline hover:text-dark-dust"
        >
          {children}
        </a>
      ),
      internalLink: ({ value, children }) => {
        const article = value?.article;
        if (!article) {
          // No published target (GFG-731/GFG-734): fall back to plain text, never a dead link.
          return <>{children}</>;
        }
        return (
          <a
            href={internalLinkHref(article.slug)}
            className="text-dust-ink underline hover:text-dark-dust"
          >
            {children}
          </a>
        );
      },
    },
    types: {
      image: ({ value }) => (
        <img
          src={value.asset.url}
          alt={value.alt}
          loading="lazy"
          className="my-4 w-full rounded-[6px] border border-card-border"
        />
      ),
      callout: ({ value }) => (
        <div className="my-4 rounded-[6px] border border-good-ochre-border bg-good-ochre-bg px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[1px] text-good-ochre">
            {value.label}
          </p>
          <p className="mt-1 text-[14px] leading-relaxed text-rodeo-black">{value.text}</p>
        </div>
      ),
    },
  };
}

export function PortableTextRenderer({
  value,
  internalLinkHref = DEFAULT_INTERNAL_LINK_HREF,
  className = "",
}: PortableTextRendererProps) {
  return (
    <div className={`space-y-4 ${className}`.trim()}>
      <PortableText<HelpPortableTextItem>
        value={value}
        components={buildComponents(internalLinkHref)}
      />
    </div>
  );
}

export default PortableTextRenderer;
